import { useQuery } from '@tanstack/react-query';
import { formatMinutes, inboxApi } from '@wa/shared';

/**
 * People, as a table.
 *
 * Not a chart: this is identity plus four measures, and a bar chart of it would show
 * one measure while hiding the rest. A table shows all four and sorts by the one
 * that matters — conversations actually finished.
 */
export function AgentTable({ from, to }: { from: string; to: string }) {
  const agents = useQuery({
    queryKey: ['report-agents', from, to],
    queryFn: () => inboxApi.reportAgents(from, to),
  });

  const rows = agents.data ?? [];

  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
      <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
        {rows.length} {rows.length === 1 ? 'person' : 'people'} worked in this period
      </h2>

      {rows.length === 0 ? (
        <p className="px-3 py-8 text-center text-small text-(--color-muted) sm:px-4">
          Nobody has resolved or sent anything in this range.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-small">
            <thead>
              <tr className="border-b border-(--color-line-dim) text-left text-micro uppercase tracking-wide text-(--color-faint)">
                <th className="px-3 py-2 font-medium sm:px-4">Person</th>
                <th className="px-2 py-2 text-right font-medium">Resolved</th>
                <th className="px-2 py-2 text-right font-medium">Sent</th>
                <th className="px-2 py-2 text-right font-medium">First reply</th>
                <th className="px-3 py-2 text-right font-medium sm:px-4">To resolve</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-(--color-line-dim)">
              {rows.map((row) => (
                <tr key={row.agentId}>
                  <td className="px-3 py-2 sm:px-4">{row.displayName}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.resolved.toLocaleString('en-IN')}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.messagesSent.toLocaleString('en-IN')}</td>
                  {/* Summed and counted in the rollup, divided here — so a week is
                      weighted by its conversations rather than by its days. */}
                  <td className="px-2 py-2 text-right tabular-nums">
                    {formatMinutes(row.frCount > 0 ? row.frSum / row.frCount : null)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums sm:px-4">
                    {formatMinutes(row.resCount > 0 ? row.resSum / row.resCount : null)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="border-t border-(--color-line-dim) px-3 py-2 text-micro text-(--color-muted) sm:px-4">
        Times are working hours, and count only replies a person typed &mdash; a bot answering
        first does not stop this clock.
      </p>
    </section>
  );
}
